import { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { normalizeLanguage } from "../../domains/app/utils/langUtils";
import { getRoomName as resolveRoomName } from "../../domains/room/hooks/useRoomName";
import {
  IconStar,
  IconSquareRoundedPlusFilled,
  IconStarFilled,
  IconPinFilled,
  IconPinnedOff,
  IconDoorEnter,
  IconDoorExit,
  IconShare2,
  IconPlanet,
} from "@tabler/icons-react";
import { EmptyState } from "../../components/ui/EmptyState";
import { RoomListItem } from "../../components/ui/RoomListItem";
import { TabButton } from "../../components/ui/TabButton";
import NavRoomsIcon from "../../assets/icons/nav/nav-rooms.svg?react";
import Header from "../../components/app/headers/Header";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import Navbar from "../../components/app/navbars/Navbar";
import Loader from "../../components/Loader";
import BottomPanel from "../../components/app/panels/BottomPanel";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { displayToast } from "../../components/app/AppToast";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { useQRCodeScanner } from "../../domains/room/hooks/useQRCodeScanner";
import { apiClient, useApiQuery, revalidateApiQuery } from "../../services/api";
import { roomHasNewContent } from "../../domains/room/utils/roomVisits";
import { useLongPress } from "../../hooks/use-long-press";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { useConfirmation } from "../../hooks/useConfirmation";
import { shareUrl } from "../../domains/app/utils/shareUrl";
import {
  gaEventPinRoom,
  gaEventUnpinRoom,
  gaEventRoomRecommendationShown,
} from "../../tools/analytics/domains/room.analytics";
import { Button } from "../../components/ui/Button";
import { useRoomsLayout } from "../../domains/room/hooks/useRoomsLayout";
import { DesktopRoomsPage } from "./desktop/DesktopRoomsPage";
import { useJoinRoom } from "../../domains/room/hooks/useJoinRoom";

function getInitials(name: string): string {
  return name
    .split(/[\s&]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

type Tab = "my-rooms" | "explore";

type RoomItemProps = {
  room: {
    id: string;
    icoModel: {
      url: string;
      sizes: { icon: { file: string } } | null;
    } | null;
    organisationModel: { name: string; logo: { url: string } };
  } & Parameters<typeof resolveRoomName>[0];
  getRoomName: (room: Parameters<typeof resolveRoomName>[0]) => string;
  newContentDates: Record<string, string | null>;
  isLast: boolean;
  onClick: () => void;
  onLongPress?: () => void;
  action?: React.ReactNode;
};

function RoomItem({
  room,
  getRoomName,
  newContentDates,
  isLast,
  onClick,
  onLongPress,
  action,
}: RoomItemProps): React.JSX.Element {
  const { t } = useTranslation();
  const { handlers, didLongPressRef } = useLongPress({
    onLongPress: onLongPress ?? (() => {}),
  });

  return (
    <div {...(onLongPress !== undefined ? handlers : {})}>
      <RoomListItem
        initials={getInitials(getRoomName(room))}
        avatarSrc={
          (room.icoModel !== null
            ? room.icoModel.sizes !== null
              ? room.icoModel.sizes.icon.file
              : room.icoModel.url
            : room.organisationModel.logo.url) || undefined
        }
        title={getRoomName(room)}
        subtitle={t("by {{name}}", { name: room.organisationModel.name })}
        newContent={roomHasNewContent(
          newContentDates[room.id] ?? null,
          room.id
        )}
        isLast={isLast}
        action={action}
        onClick={() => {
          if (didLongPressRef.current) {
            didLongPressRef.current = false;
            return;
          }
          onClick();
        }}
      />
    </div>
  );
}

/**
 * A recommended room in the Explore list, counted as SHOWN once it mounts.
 *
 * The personalized section sits at the very top of Explore and holds a handful
 * of rooms, so mounting IS reaching the screen here, the same assumption the
 * feed's promo card makes. A catalogue long enough to push these rows below the
 * fold would need an intersection observer instead of a mount.
 *
 * A component rather than a call in the page body because the section is
 * conditional and a hook cannot be: this is the only place the impression may
 * fire, and it fires once per room.
 */
function RecommendedRoomItem({
  room,
  getRoomName,
  ...rest
}: RoomItemProps): React.JSX.Element {
  useTrackOnMount(
    "room-recommendation-shown",
    { status: "shown", key: `rooms-center:${room.id}` },
    () => {
      gaEventRoomRecommendationShown({
        roomId: room.id,
        roomName: getRoomName(room),
        surface: "rooms-center",
      });
    }
  );

  return <RoomItem room={room} getRoomName={getRoomName} {...rest} />;
}

/**
 * Join, in the long-press sheet of a room. The room joins in place, and its
 * welcome sheet opens over the list (docs/partner-objection.md, REQ-05).
 */
function JoinRoomAction({
  roomId,
  onJoin,
}: {
  roomId: string;
  onJoin: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { joinRoom, isJoining } = useJoinRoom(roomId, "public app", "greet");
  // The query `useJoinRoom` reads: a join before the room loads does nothing.
  const roomQuery = useApiQuery("/webapp/private/rooms/:id", {
    param: { id: roomId },
  });
  return (
    <button
      type="button"
      disabled={isJoining || roomQuery.state !== "success"}
      className="flex items-center gap-4 px-6 py-5 text-left active:bg-ds-slate-100"
      onClick={() => {
        onJoin();
        void joinRoom();
      }}
    >
      <IconDoorEnter className="size-5 shrink-0 text-ds-slate-500" />
      <span className="text-[15px]/5 font-semibold  text-ds-black">
        {t("Join room")}
      </span>
    </button>
  );
}

export default function RoomsCenterPage(): React.JSX.Element {
  const roomsLayout = useRoomsLayout();
  switch (roomsLayout) {
    case "desktop":
      return <DesktopRoomsPage />;
    case "mobile":
      return <MobileRoomsCenterPage />;
    default:
      throw new Error(roomsLayout satisfies never);
  }
}

function MobileRoomsCenterPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = normalizeLanguage(i18n.resolvedLanguage);
  const history = useHistory();
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const { handleQRCodeScan } = useQRCodeScanner();

  const tabFromUrl = new URLSearchParams(location.search).get("tab");
  // "for-you" was a third tab whose rooms are now the top section of Explore.
  // Kept as an alias so a link shared while it existed still lands on them.
  const activeTab: Tab =
    tabFromUrl === "explore" || tabFromUrl === "for-you"
      ? "explore"
      : "my-rooms";

  const setActiveTab = useCallback(
    (tab: Tab) => {
      const params = new URLSearchParams(location.search);
      if (tab === "my-rooms") {
        params.delete("tab");
      } else {
        params.set("tab", tab);
      }
      const search = params.toString();
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : "",
      });
    },
    [history, location.pathname, location.search]
  );

  const getRoomName = useCallback(
    (room: Parameters<typeof resolveRoomName>[0]): string =>
      resolveRoomName(room, lang),
    [lang]
  );

  const collator = new Intl.Collator(i18n.resolvedLanguage, {
    sensitivity: "base",
  });
  const sortByName = <T extends Parameters<typeof resolveRoomName>[0]>(
    rooms: Array<T>
  ): Array<T> =>
    [...rooms].sort((a, b) => collator.compare(getRoomName(a), getRoomName(b)));

  const myRoomsQuery = useApiQuery(
    "/webapp/private/rooms",
    currentUser === null ? false : {}
  );

  const publicRoomsQuery = useApiQuery("/webapp/public/rooms", {});
  const recommendedRoomsQuery = useApiQuery(
    "/webapp/private/rooms/recommended",
    currentUser === null ? false : {}
  );
  const newContentQuery = useApiQuery(
    "/webapp/private/rooms/new-content",
    currentUser === null ? false : {}
  );
  const newContentDates: Record<string, string | null> =
    newContentQuery.state === "success" ? newContentQuery.body : {};

  const myRooms =
    myRoomsQuery.state === "success" ? myRoomsQuery.body.rooms : [];
  const publicRooms =
    publicRoomsQuery.state === "success" ? publicRoomsQuery.body : [];
  const recommendedRooms =
    recommendedRoomsQuery.state === "success"
      ? recommendedRoomsQuery.body.rooms
      : [];

  const myRoomIds = new Set(myRooms.map((r) => r.id));
  // Explore's two sections partition one catalogue: a recommended room never
  // repeats in the alphabetical remainder under it. `/rooms/recommended`
  // already excludes joined rooms server-side, but the membership list here is
  // revalidated on a join while that response is still cached, so the room the
  // user just joined would linger for one render without this filter.
  const recommendedNotJoined = recommendedRooms.filter(
    (r) => !myRoomIds.has(r.id)
  );
  const recommendedIds = new Set(recommendedNotJoined.map((r) => r.id));
  const otherPublicRooms = sortByName(
    publicRooms.filter((r) => !myRoomIds.has(r.id) && !recommendedIds.has(r.id))
  );

  // Pinned room
  type LongPressRoom = { id: string } & Parameters<typeof resolveRoomName>[0];
  const [longPressedRoom, setLongPressedRoom] = useState<LongPressRoom | null>(
    null
  );
  const leaving = useConfirmation<LongPressRoom>();
  const pinnedRoomId = currentUser === null ? null : currentUser.pinnedRoom;
  const pinnedRoom =
    pinnedRoomId === null
      ? null
      : (myRooms.find((r) => r.id === pinnedRoomId) ?? null);
  const unpinnedRooms = sortByName(
    pinnedRoom !== null
      ? myRooms.filter((r) => r.id !== pinnedRoom.id)
      : myRooms
  );
  const isLongPressedRoomJoined =
    longPressedRoom !== null && myRoomIds.has(longPressedRoom.id);
  const isLongPressedRoomPinned =
    longPressedRoom !== null && longPressedRoom.id === pinnedRoom?.id;
  // "May I offer Join for this room?" is now ASKED rather than computed here.
  // It used to read `acceptanceCriteria` off the public room card — which
  // countries, professions and specialties a client admits — and that policy no
  // longer travels on a public payload (SEC-07). The server owns the question:
  // `join/check` answers false for a room already joined, for one whose criteria
  // the caller fails, and for an invite-only room.
  //
  // Gated on `!isLongPressedRoomJoined` as well, so the default My-rooms tab —
  // where every row is already joined and the answer is a foregone `false` —
  // spends no round trip. `durable` because a room's joinability does not move
  // inside a session, and the sheet reopens on the same rooms.
  //
  // An error folds into "do not offer", which is what the old code did on every
  // failure too. `onRequestError` still reports it.
  const joinCheckQuery = useApiQuery(
    "/webapp/private/rooms/:id/join/check",
    longPressedRoom !== null && currentUser !== null && !isLongPressedRoomJoined
      ? { param: { id: longPressedRoom.id }, durable: true }
      : false
  );
  const canOfferJoin =
    joinCheckQuery.state === "success" && joinCheckQuery.body;

  async function leaveRoom(
    roomToLeave: LongPressRoom
  ): Promise<"done" | "failed"> {
    try {
      await apiClient.delete("/webapp/private/rooms/:id/leave", {
        param: { id: roomToLeave.id },
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return "failed";
    }
    displayToast(t("You've left the space"), "success");
    await revalidateApiQuery("/webapp/private/rooms");
    void revalidateApiQuery("/webapp/private/playlists/recommended");
    // The leave flow always long-presses the room first, so `join/check` holds
    // a `false` for it — the answer for a room the user was still in. Without
    // this the Join row is missing from the next long-press of the one room
    // the user is most likely to want back.
    await revalidateApiQuery("/webapp/private/rooms/:id/join/check", {
      param: { id: roomToLeave.id },
    });
    void reloadCurrentUser();
    return "done";
  }

  return (
    <ScrollableLayout
      header={<Header onTitleClick={null} title={t("Rooms")} />}
      footer={<Navbar />}
      className="flex flex-col justify-between"
    >
      <div className="flex flex-col gap-3 px-5 pb-24 pt-4">
        <div className="-mx-5 flex items-center gap-3 overflow-x-auto px-5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] scrollbar-none">
          <TabButton
            active={activeTab === "my-rooms"}
            icon={<IconStar className="size-4" />}
            activeIcon={<IconStarFilled className="size-4" />}
            onClick={() => {
              setActiveTab("my-rooms");
            }}
          >
            {t("My rooms")}
          </TabButton>
          <TabButton
            active={activeTab === "explore"}
            icon={<IconPlanet className="size-4" />}
            activeIcon={<IconPlanet className="size-4" />}
            onClick={() => {
              setActiveTab("explore");
            }}
          >
            {t("Explore")}
          </TabButton>
        </div>

        {/* My Rooms tab */}
        {activeTab === "my-rooms" && (
          <>
            {myRoomsQuery.state === "pending" ? (
              <Loader expanded />
            ) : myRooms.length === 0 ? (
              <div className="flex flex-col items-center">
                <EmptyState
                  className="my-8"
                  icon={<NavRoomsIcon className="size-8 text-ds-mint-800" />}
                  title={t("No rooms joined yet")}
                  description={t(
                    "Discover rooms made for you. Follow curated content by specialty, in real time"
                  )}
                />
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setActiveTab("explore");
                  }}
                >
                  {t("Explore rooms")}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col">
                {pinnedRoom !== null && (
                  <div className="-mx-1 rounded-2xl px-1">
                    <RoomItem
                      room={pinnedRoom}
                      getRoomName={getRoomName}
                      newContentDates={newContentDates}
                      isLast={unpinnedRooms.length === 0}
                      onLongPress={() => {
                        setLongPressedRoom(pinnedRoom);
                      }}
                      onClick={() => {
                        history.push(`/room/discovery/${pinnedRoom.id}`, {
                          roomsSearch: location.search,
                        });
                      }}
                      action={
                        <IconPinFilled className="mr-2 mt-2 size-4 text-ds-slate-400" />
                      }
                    />
                  </div>
                )}
                {unpinnedRooms.map((room, i) => (
                  <RoomItem
                    key={room.id}
                    room={room}
                    getRoomName={getRoomName}
                    newContentDates={newContentDates}
                    isLast={i === unpinnedRooms.length - 1}
                    onLongPress={() => {
                      setLongPressedRoom(room);
                    }}
                    onClick={() => {
                      history.push(`/room/discovery/${room.id}`, {
                        roomsSearch: location.search,
                      });
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Explore tab: the personalized section on top of the catalogue */}
        {activeTab === "explore" && (
          <>
            {publicRoomsQuery.state === "pending" ||
            recommendedRoomsQuery.state === "pending" ? (
              <Loader expanded />
            ) : recommendedNotJoined.length === 0 &&
              otherPublicRooms.length === 0 ? (
              <EmptyState
                className="my-8"
                icon={<IconPlanet className="size-8 text-ds-mint-800" />}
                title={t("You've joined every room")}
                description={t(
                  "New rooms open regularly, and they show up here first."
                )}
              />
            ) : (
              <div className="flex flex-col gap-2 py-2">
                {recommendedNotJoined.length > 0 && (
                  <div className="flex flex-col">
                    <h2 className="text-base font-bold leading-none text-primary-black">
                      {t("Matched to your interests")}
                    </h2>
                    {recommendedNotJoined.map((room, i) => (
                      <RecommendedRoomItem
                        key={room.id}
                        room={room}
                        getRoomName={getRoomName}
                        newContentDates={newContentDates}
                        isLast={i === recommendedNotJoined.length - 1}
                        onLongPress={() => {
                          setLongPressedRoom(room);
                        }}
                        onClick={() => {
                          history.push(`/room/discovery/${room.id}`, {
                            roomsSearch: location.search,
                          });
                        }}
                        action={
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              history.push(`/room/discovery/${room.id}`, {
                                roomsSearch: location.search,
                              });
                            }}
                          >
                            {t("Discover")}
                          </Button>
                        }
                      />
                    ))}
                  </div>
                )}
                {otherPublicRooms.length > 0 && (
                  <div className="flex flex-col">
                    <h2 className="text-base font-bold leading-none text-primary-black">
                      {t("Discover new rooms")}
                    </h2>
                    {otherPublicRooms.map((room, i) => (
                      <RoomItem
                        key={room.id}
                        room={room}
                        getRoomName={getRoomName}
                        newContentDates={newContentDates}
                        isLast={i === otherPublicRooms.length - 1}
                        onLongPress={() => {
                          setLongPressedRoom(room);
                        }}
                        onClick={() => {
                          history.push(`/room/discovery/${room.id}`, {
                            roomsSearch: location.search,
                          });
                        }}
                        action={
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              history.push(`/room/discovery/${room.id}`, {
                                roomsSearch: location.search,
                              });
                            }}
                          >
                            {t("Discover")}
                          </Button>
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating add-room button */}
      <Button
        type="button"
        variant="primary"
        mode="iconOnly"
        className="fixed right-5 z-10 size-12 rounded-2xl shadow-[0px_3px_3px_0px_rgba(0,0,0,0.09),0px_7px_4px_0px_rgba(0,0,0,0.05),0px_12px_4px_0px_rgba(0,0,0,0.01)]"
        style={{ bottom: "calc(6rem + var(--safe-area-bottom))" }}
        onClick={handleQRCodeScan}
      >
        <span className="flex items-center justify-center">
          <IconSquareRoundedPlusFilled className="size-7" />
        </span>
      </Button>

      {/* Room actions bottom panel */}
      <BottomPanel
        isOpen={longPressedRoom !== null}
        onClose={() => {
          setLongPressedRoom(null);
        }}
        title={
          longPressedRoom !== null ? getRoomName(longPressedRoom) : undefined
        }
      >
        <div className="flex flex-col">
          {/* Share room */}
          {longPressedRoom !== null && (
            <button
              type="button"
              className="flex items-center gap-4 px-6 py-5 text-left active:bg-ds-slate-100"
              onClick={() => {
                const roomName = getRoomName(longPressedRoom);
                const url = `${globalThis.location.origin}/room/discovery/${longPressedRoom.id}`;
                const myMatch = myRooms.find(
                  (r) => r.id === longPressedRoom.id
                );
                const publicMatch = publicRooms.find(
                  (r) => r.id === longPressedRoom.id
                );
                const fullRoom =
                  myMatch !== undefined
                    ? myMatch
                    : publicMatch !== undefined
                      ? publicMatch
                      : null;
                const orgName =
                  fullRoom === null ? "" : fullRoom.organisationModel.name;
                const shareText =
                  orgName.trim().length === 0
                    ? t(
                        "{{roomName}} Room - curated by Juisci. Independent scientific selection: peer-reviewed publications, guidelines, and expert content for Healthcare Professionals.",
                        { roomName }
                      )
                    : t(
                        "{{roomName}} Room - curated by Juisci, supported by {{orgName}}. Independent scientific selection: peer-reviewed publications, guidelines, and expert content for Healthcare Professionals.",
                        { roomName, orgName }
                      );
                setLongPressedRoom(null);
                void shareUrl(roomName, shareText, Promise.resolve(url));
              }}
            >
              <IconShare2 className="size-5 shrink-0 text-ds-slate-500" />
              <span className="text-[15px]/5 font-semibold  text-ds-black">
                {t("Share room")}
              </span>
            </button>
          )}

          {/* Pin / Unpin — only for joined rooms */}
          {isLongPressedRoomJoined && (
            <button
              type="button"
              className="flex items-center gap-4 px-6 py-5 text-left active:bg-ds-slate-100"
              onClick={() => {
                const roomName = getRoomName(longPressedRoom);
                const roomId = longPressedRoom.id;
                const isPinned = isLongPressedRoomPinned;
                const promise = isPinned
                  ? apiClient.delete("/webapp/private/users/me/pinned-room", {})
                  : apiClient.post("/webapp/private/users/me/pinned-room", {
                      json: { roomId },
                    });
                void promise
                  .then(() => {
                    if (isPinned) gaEventUnpinRoom({ roomId, roomName });
                    else gaEventPinRoom({ roomId, roomName });
                    void reloadCurrentUser();
                  })
                  .catch(() => {
                    displayToast(
                      t("Something went wrong. Please try again."),
                      "error"
                    );
                  })
                  .finally(() => {
                    setLongPressedRoom(null);
                  });
              }}
            >
              {isLongPressedRoomPinned ? (
                <IconPinnedOff className="size-5 shrink-0 text-ds-slate-500" />
              ) : (
                <IconPinFilled className="size-5 shrink-0 text-ds-slate-500" />
              )}
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px]/5 font-semibold  text-ds-black">
                  {isLongPressedRoomPinned ? t("Unpin room") : t("Pin to top")}
                </span>
                <span className="text-[12px]/4  text-ds-slate-500">
                  {isLongPressedRoomPinned
                    ? t(
                        "The Discovery page will be shown by default when you open the app"
                      )
                    : t(
                        "This room will be the default one you see when you open the app"
                      )}
                </span>
              </div>
            </button>
          )}

          {/* Leave room — only for joined rooms */}
          {isLongPressedRoomJoined && (
            <button
              type="button"
              className="flex items-center gap-4 px-6 py-5 text-left active:bg-ds-slate-100"
              onClick={() => {
                // The row shows only for a long-pressed room.
                if (longPressedRoom === null)
                  throw new Error("should not happen: no long-pressed room");
                leaving.ask(longPressedRoom);
                setLongPressedRoom(null);
              }}
            >
              <IconDoorExit className="size-5 shrink-0 text-ds-strawberry-500" />
              <span className="text-[15px]/5 font-semibold  text-ds-strawberry-500">
                {t("Leave room")}
              </span>
            </button>
          )}

          {/* Join room — only for non-joined rooms the user is eligible for */}
          {!isLongPressedRoomJoined &&
            canOfferJoin &&
            longPressedRoom !== null && (
              <JoinRoomAction
                roomId={longPressedRoom.id}
                onJoin={() => {
                  setLongPressedRoom(null);
                }}
              />
            )}
        </div>
      </BottomPanel>

      {/* Leave room confirmation */}
      <ConfirmModal
        isVisible={leaving.state.status === "confirming"}
        title={t("Leave this Room?")}
        promptText={t(
          "You'll lose access to exclusive content and updates from this community. You can always rejoin later."
        )}
        cancelText={t("Cancel")}
        confirmText={t("Leave Room")}
        confirmDanger
        onConfirm={() => {
          void leaving.confirm(leaveRoom);
        }}
        onCancel={leaving.cancel}
      />
    </ScrollableLayout>
  );
}
